import { PublicTemplateConfigurationProps } from '~/components/Templates/components/FlowTemplates/PublicTemplateConfigurationModal';
import { useRootStore } from '~/stores/useRootStore';
import { useIsGateEnabled } from '~/common/Statsig/useIsGateEnabled';
import { useEffect, useState, useMemo } from 'react';
import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import { useGlobalMX } from '~/providers/mx-global-context/useGlobalMX';
import { useTranslation } from 'react-i18next';
import { NAMESPACES } from '~/i18n/namespaces';
import { trackError } from '~/services/logger/error.utils';

export const useConfigureSellingLeadForm = ({
    publicConfiguration
}: PublicTemplateConfigurationProps) => {
    const {
        templatesUiStore: {
            selectedTemplate,
            updateTemplatePublicConfiguration,
            leadForms
        },
        flowStore,
        templatesCtrl
    } = useRootStore();
    const { toastStore } = useGlobalMX();

    const isPartner = useIsGateEnabled({
        gateName: 'template_selling'
    });

    const [isDisabled, setIsDisabled] = useState(false);

    const { t } = useTranslation(NAMESPACES.TEMPLATES, {
        keyPrefix: 'share_template.publish_template'
    });

    useEffect(() => {
        if (isPartner && !leadForms?.length) {
            templatesCtrl.fetchLeadForms({ isLeadFormEnabled: true });
        }
    }, [isPartner, leadForms?.length, templatesCtrl]);

    const updateSellingLeadForm = async (leadFormId: string) => {
        try {
            setIsDisabled(true);
            const res =
                await templatesCtrl.updatePublicConfigurationSelledByTemplate(
                    selectedTemplate._id,
                    leadFormId
                );
            updateTemplatePublicConfiguration(res);
            if (flowStore.flows[res._id]) {
                flowStore.updateFlow(res._id, {
                    public_configuration: res.public_configuration
                });
            }
        } catch (error) {
            toastStore.add({
                text:
                    error?.data?.error_message ||
                    t('partners.selling_lead_form_error'),
                type: ToastType.Error
            });
            trackError({
                error,
                message:
                    '[SellingLeadForm] error connecting lead form to public configuration',
                context: {
                    errMsg: error?.data?.error_messgae,
                    leadfFormId: leadFormId,
                    templateId: selectedTemplate._id
                }
            });
        } finally {
            setIsDisabled(false);
        }
    };

    const isPublished = publicConfiguration?.published_template;

    const selledLeadFormOptions: { label: string; value: string }[] = useMemo(
        () =>
            leadForms?.map(leadForm => ({
                value: leadForm._id,
                label: leadForm.title
            })),
        [leadForms]
    );

    const setSelledByLeadForm = async (value: string | null) => {
        setIsDisabled(true);
        await updateSellingLeadForm(value);
        setIsDisabled(false);
    };

    const shouldShowSellingToggle =
        isPublished && isPartner && selledLeadFormOptions;

    return {
        selledLeadFormOptions,
        isPartner,
        isDisabled,
        setSelledByLeadForm,
        shouldShowSellingToggle
    };
};
